import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2 } from "lucide-react";
import { API } from "@/config";

export const TrialsList = () => {
  const navigate = useNavigate();
  const [trials, setTrials] = useState([]);

  useEffect(() => { fetchTrials(); }, []);

  const fetchTrials = async () => {
    try { const response = await axios.get(`${API}/trials`); setTrials(response.data); }
    catch (error) { toast.error("Error fetching trial agreements"); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this trial agreement?")) {
      try { await axios.delete(`${API}/trials/${id}`); toast.success("Trial agreement deleted"); fetchTrials(); }
      catch (error) { toast.error("Error deleting trial agreement"); }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'returned': return 'bg-blue-100 text-blue-800';
      case 'converted': return 'bg-purple-100 text-purple-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" data-testid="trials-title">Demo Device Trials</h1>
        <Button onClick={() => navigate('/trials/create')} data-testid="create-trial-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Trial Agreement
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trial #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>Trial Period</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No trial agreements yet. Create your first one!
                  </TableCell>
                </TableRow>
              ) : (
                trials.map((trial) => (
                  <TableRow key={trial.id} data-testid={`trial-row-${trial.id}`}>
                    <TableCell className="font-medium">{trial.trial_number}</TableCell>
                    <TableCell>{trial.customer_name}</TableCell>
                    <TableCell>{trial.device_name}</TableCell>
                    <TableCell className="text-sm">
                      {new Date(trial.trial_start).toLocaleDateString()} - {new Date(trial.trial_end).toLocaleDateString()}
                    </TableCell>
                    <TableCell>${trial.estimated_value.toFixed(2)}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(trial.status)}`} data-testid={`trial-status-${trial.id}`}>
                        {trial.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/trials/${trial.id}`)} data-testid={`view-trial-${trial.id}`}>
                        <FileText className="h-4 w-4" />
                      </Button>
                      {trial.status === 'active' && (
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/trials/${trial.id}/edit`)} data-testid={`edit-trial-${trial.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(trial.id)} data-testid={`delete-trial-${trial.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
